import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import Layout from '../../layout/Layout';
import OrderChartWidget from '../../components/OrderChartWidget/OrderChartWidget';
import WarningOrderWidget from '../../components/WarningOrderWidget/WarningOrderWidget';
import MetricWidget from './components/MetricWidget';
import routes from '../../router/routes';
import { permissions } from '../../common/permissionGroup';

const widgets = [
  {
    id: 'ordersChart',
    label: 'Orders chart',
    permission: permissions.COW_READ,
    wide: true,
    render: () => <OrderChartWidget />
  },
  {
    id: 'priorityOrders',
    label: 'High priority orders',
    permission: permissions.COW_READ,
    render: () => <WarningOrderWidget />
  },
  {
    id: 'expectedToday',
    label: 'Expected deliveries today',
    permission: permissions.COW_READ,
    render: () => (
      <MetricWidget
        title="Expected deliveries today"
        description="Ordered parts due today"
        endpoint="/co/acceptance_to_day"
        path={routes.ACCEPTANCE}
      />
    )
  },
  {
    id: 'lateDeliveries',
    label: 'Late deliveries (5 days)',
    permission: permissions.COW_READ,
    render: () => (
      <MetricWidget
        title="Late deliveries (5 days)"
        description="Ordered parts due in the last five days"
        endpoint="/co/acceptance_delays"
        path={routes.ACCEPTANCE}
        color="text-danger"
      />
    )
  },
  {
    id: 'failedJobs',
    label: 'Failed jobs',
    permission: permissions.JOBS_READ,
    render: () => (
      <MetricWidget
        title="Failed jobs"
        description="Background tasks requiring attention"
        endpoint="/jobs/failed"
        path={routes.JOBS_FAILED}
        color="text-warning"
      />
    )
  }
];

const defaultWidgetIds = widgets.map(({ id }) => id);

const Dashboard = () => {
  const { user, permissions: grantedPermissions = [] } = useSelector((state) => state.getMyProfile);
  const [visibleIds, setVisibleIds] = useState(defaultWidgetIds);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const userId = user?.id;

  useEffect(() => {
    if (!userId) return;
    try {
      const saved = JSON.parse(localStorage.getItem(`dashboard-widgets:v1:${userId}`));
      setVisibleIds(Array.isArray(saved)
        ? saved.filter((id) => defaultWidgetIds.includes(id))
        : defaultWidgetIds);
    } catch (_) {
      setVisibleIds(defaultWidgetIds);
    }
  }, [userId]);

  const canView = (requiredPermission) => {
    const [requiredGroup, requiredAction] = requiredPermission.split(':');
    return grantedPermissions.some(({ group, permission }) => (
      (group === 'admin' && permission === 'admin')
      || (group === requiredGroup && permission === requiredAction)
    ));
  };
  const availableWidgets = widgets.filter(({ permission }) => canView(permission));
  const visibleWidgets = availableWidgets.filter(({ id }) => visibleIds.includes(id));
  const wideWidget = visibleWidgets.find(({ wide }) => wide);
  const smallWidgets = visibleWidgets.filter(({ wide }) => !wide);
  const hasSidebar = Boolean(wideWidget && smallWidgets.length);

  const toggleWidget = (id) => {
    const next = visibleIds.includes(id)
      ? visibleIds.filter((widgetId) => widgetId !== id)
      : [...visibleIds, id];
    setVisibleIds(next);
    if (userId) {
      try {
        localStorage.setItem(`dashboard-widgets:v1:${userId}`, JSON.stringify(next));
      } catch (_) {
        // The setting still works for this session if local storage is unavailable.
      }
    }
  };

  const breadcrumbs = {
    pageTitle: 'Dashboard',
    history: [{ title: 'Home' }, { title: 'Dashboard' }]
  };

  return (
    <Layout breadcrumbs={breadcrumbs}>
      <div className="flex justify-end mb-[16px]">
        <button
          type="button"
          className="px-[14px] py-[9px] rounded-[6px] bg-primary-light text-primary"
          onClick={() => setSettingsOpen(!settingsOpen)}
          aria-expanded={settingsOpen}
        >
          Customize widgets
        </button>
      </div>
      {settingsOpen && (
        <div className="bg-white dark:bg-[#1b263b] rounded-[8px] shadow-card p-[16px] mb-[20px]">
          <div className="font-semibold mb-[10px]">Show on dashboard</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[10px]">
            {availableWidgets.map(({ id, label }) => (
              <label
                key={id}
                htmlFor={`dashboard-widget-${id}`}
                className="flex items-center gap-[8px] cursor-pointer"
              >
                <input
                  id={`dashboard-widget-${id}`}
                  type="checkbox"
                  checked={visibleIds.includes(id)}
                  onChange={() => toggleWidget(id)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      {userId && visibleWidgets.length > 0 && (
        <div
          className={hasSidebar
            ? 'grid grid-cols-1 xl:grid-cols-3 gap-[20px] items-start'
            : 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-[20px] items-start'}
        >
          {wideWidget && (
            <div className={hasSidebar ? 'min-w-0 xl:col-span-2' : 'min-w-0 xl:col-span-3'}>
              {wideWidget.render()}
            </div>
          )}
          {smallWidgets.length > 0 && (
            <div
              className={hasSidebar
                ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-[20px] min-w-0'
                : 'contents'}
            >
              {smallWidgets.map((widget) => (
                <div key={widget.id} className="min-w-0">{widget.render()}</div>
              ))}
            </div>
          )}
        </div>
      )}
      {userId && availableWidgets.length > 0 && visibleWidgets.length === 0 && (
        <div className="bg-white dark:bg-[#1b263b] rounded-[8px] p-[24px] text-center">
          All widgets are hidden. Use Customize widgets to show one.
        </div>
      )}
      {userId && availableWidgets.length === 0 && (
        <div className="bg-white dark:bg-[#1b263b] rounded-[8px] p-[24px] text-center">
          No dashboard widgets are available for your permissions.
        </div>
      )}
    </Layout>
  );
};

export default Dashboard;
